import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { Spinner } from 'react-bootstrap';
import { toast } from 'sonner';

import { getAllOrganisationData } from '../utils/api/organisationAPI';
import { razorPayInitiatePayment } from '../utils/api/PaymentAPI';
import { getAllPromoCode } from '../utils/api/promoCode';
import { calculatePricingWithMinutes } from '../utils/api/creditPurchase';
import PaymentUrlModal from './child/PaymentUrlModal';

const PaymentUrlLayer = () => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [minutes, setMinutes] = useState(1);

  const [promoCodeId, setPromoCodeId] = useState('');
  const [activePromos, setActivePromos] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);

  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricing, setPricing] = useState(null);

  const [paymentUrl, setPaymentUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Map promos by id for quick lookup
  const promoById = useMemo(() => {
    const map = {};
    for (const p of activePromos) map[p.id || p._id] = p;
    return map;
  }, [activePromos]);

  // INR formatter
  const inr = (v) => `₹${Number(v ?? 0).toFixed(2)}`;

  // Load orgs
  useEffect(() => {
    getAllOrganisationData({ setIsLoading })
      .then((data) => setOrganizations(data?.organizations || []))
      .catch((err) => toast.error(err?.message || 'Failed to fetch organizations'));
  }, []);

  // Load active promos
  useEffect(() => {
    let mounted = true;
    setPromoLoading(true);
    getAllPromoCode({ isActive: true })
      .then((res) => {
        const list = res?.data?.promoCodes || [];
        if (mounted) setActivePromos(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error(err);
        toast.error(err?.message || 'Failed to fetch promo codes');
      })
      .finally(() => setPromoLoading(false));
    return () => { mounted = false; };
  }, []);

  // Calculate pricing
  useEffect(() => {
    const m = parseInt(minutes, 10);
    if (!Number.isFinite(m) || m <= 0) {
      setPricing(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setPricingLoading(true);
        const payload = { minutes: m };
        if (promoCodeId) payload.promoCodeId = promoCodeId;

        const res = await calculatePricingWithMinutes(payload);
        // Expected shape per your spec: { message, pricing: { ... } }
        const p = res?.pricing || res?.data?.pricing || null;
        if (!p) throw new Error('No pricing response');

        if (!cancelled) setPricing(p);
      } catch (err) {
        console.error('Pricing calc error:', err);
        if (!cancelled) {
          setPricing(null);
          toast.error(err?.message || 'Failed to calculate pricing');
        }
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [minutes, promoCodeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !minutes) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        organizationId: selectedOrg,
        minutes: parseInt(minutes, 10),
        promoCodeId: promoCodeId || undefined,
      };
      const { data, message } = await razorPayInitiatePayment(payload);
      setPaymentUrl(data?.paymentUrl || '');
      setShowModal(true);
      if (message) toast.success(message);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to generate payment URL');
    } finally {
      setIsLoading(false);
    }
  };

  // ----- Price Summary -----
const renderPriceSummary = () => {
  if (pricingLoading && !pricing) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 120 }}>
        <Spinner animation="border" size="sm" />
        <span className="ms-2">Calculating price…</span>
      </div>
    );
  }

  if (!pricing) {
    return (
      <div className="text-muted text-center" style={{ fontSize: 14, padding: 16 }}>
        Enter minutes to see the price summary.
      </div>
    );
  }

  const tier = pricing?.tier ?? '—';
  const minutesNum = Number(pricing?.minutes ?? minutes) || 0;
  const rate = Number(pricing?.ratePerMinute ?? 0);

  // server-calculated values
  const base = Number(pricing?.baseAmount ?? 0);
  const discount = Number(pricing?.discountAmount ?? 0);
  const finalAmount = Number(pricing?.finalAmount ?? base);      // after discount, before GST
  const tax = Number(pricing?.taxAmount ?? 0);                    // GST on finalAmount
  const total = Number(pricing?.totalAmount ?? finalAmount + tax);
  const gstPct = finalAmount > 0 ? Math.round((tax / finalAmount) * 100) : 18;

  const Row = ({ label, value, strong = false, valueClass = {} }) => (
    <div className="d-flex align-items-center justify-content-between" style={{ padding: '6px 2px' }}>
      <span className="text-muted" style={{ fontSize: 14 }}>{label}</span>
      <span style={{ fontWeight: strong ? 800 : 600, fontSize: strong ? 18 : 16, ...valueClass }}>
        {inr(value)}
      </span>
    </div>
  );

  const promoCodeObj = pricing?.promoCode;

  return (
    <div className="shadow-sm border" style={{ background: '#fff', borderColor: '#e5e7eb', borderRadius: 12, padding: 16 }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 10 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{tier}</span>
        <span className="badge" style={{ backgroundColor: '#16a34a', color: '#fff', fontWeight: 700, borderRadius: 9999, padding: '6px 10px', letterSpacing: 0.2 }}>
          {minutesNum} Credits @{rate.toFixed(2)}
        </span>
      </div>

      {/* Base and Discount BEFORE GST */}
      <Row label="Base Price" value={base} />
      {promoCodeObj && discount > 0 && (
        <div className="d-flex align-items-center justify-content-between" style={{ padding: '6px 2px' }}>
          <span className="text-muted" style={{ fontSize: 14 }}>
            Promo Discount (<em style={{ fontStyle: 'italic' }}>{promoCodeObj.code}</em>)
          </span>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#16a34a' }}>- {inr(discount)}</span>
        </div>
      )}

      <hr className="my-3" style={{ opacity: 0.15 }} />

      {/* Order Summary */}
      <div
        className="d-flex align-items-center"
        style={{ fontSize: 18, fontWeight: 700, color: '#111827', background: '#F8FAFC', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}
      >
        Order Summary
      </div>

      {/* Subtotal = finalAmount (after discount, before GST) */}
      <Row label="Subtotal" value={finalAmount} />

      {/* GST on discounted subtotal */}
      <Row label={`GST ${gstPct}%`} value={tax} />

      {/* Total = finalAmount + tax */}
      <div className="d-flex align-items-center justify-content-between" style={{ padding: '8px 2px', marginTop: 6 }}>
        <span className="text-dark" style={{ fontSize: 18, fontWeight: 800 }}>Total</span>
        <span style={{ fontWeight: 800, fontSize: 20, color: '#16a34a' }}>
          {inr(total)}
        </span>
      </div>
    </div>
  );
};
// ----- /Price Summary -----


  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="col-lg-12">
      <div className="card card-style">
        <div className="card-header card-header-style">
          <h5 className="card-title mb-0 title-style">
            <Icon icon="tabler:link" className="me-2" width="22" height="22" />
            Generate Payment URL
          </h5>
        </div>

        <div className="card-body card-body-style">
          <form className="row gy-4" onSubmit={handleSubmit}>
            {/* Organization */}
            <div className="col-md-6">
              <label className="form-label label-style">Select Organization*</label>
              <div className="position-relative">
                <Icon icon="lucide:building" width="18" height="18" className="icon-style" />
                <select
                  name="organizationId"
                  value={selectedOrg}
                  onChange={(e) => setSelectedOrg(e.target.value)}
                  className="form-control select-style"
                  required
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.id || org._id} value={org.id || org._id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" width="18" height="18" className="dropdown-icon-style" />
              </div>
            </div>

            {/* Minutes */}
            <div className="col-md-6">
              <label className="form-label label-style">Minutes*</label>
              <div style={{ position: 'relative' }}>
                <Icon icon="lucide:clock" width="18" height="18" className="icon-style" />
                <input
                  type="number"
                  name="minutes"
                  value={minutes}
                  min={1}
                  onChange={(e) => setMinutes(e.target.value)}
                  className="form-control input-style"
                  placeholder="Enter minutes"
                  required
                />
              </div>
            </div>

            {/* Promo Code (Active) */}
            <div className="col-md-6">
              <label className="form-label label-style">
                Promo Code (Active) <span className="text-muted">(Optional)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <Icon icon="lucide:ticket" width="18" height="18" className="icon-style" />
                <select
                  name="promoCodeId"
                  value={promoCodeId}
                  onChange={(e) => setPromoCodeId(e.target.value)}
                  className="form-control select-style"
                  disabled={promoLoading}
                >
                  <option value="">{promoLoading ? 'Loading...' : 'Select promo code'}</option>
                  {activePromos.map((p) => (
                    <option key={p.id || p._id} value={p.id || p._id}>
                      {p.code}
                    </option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" width="18" height="18" className="dropdown-icon-style" />
              </div>
            </div>

            {/* Spacer to keep grid tidy */}
            <div className="col-md-6"></div>

            {/* Price Summary: FULL WIDTH, BELOW ALL FIELDS */}
            <div className="col-12">
              {renderPriceSummary()}
            </div>

            {/* Submit */}
            <div className="col-12 d-flex justify-content-end" style={{ gap: 12 }}>
              <button
                type="submit"
                className="submit-btn-style"
                disabled={isLoading || pricingLoading || !pricing}
                title={!pricing ? 'Enter valid minutes to enable' : undefined}
              >
                {isLoading ? <Spinner animation="border" size="sm" /> : 'Generate Payment URL'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <PaymentUrlModal
          show={showModal}
          onClose={() => setShowModal(false)}
          paymentUrl={paymentUrl}
        />
      )}
    </motion.div>
  );
};

export default PaymentUrlLayer;
